import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Shield,
  Database,
  LogOut,
  Settings,
  Menu,
  X,
  Sparkles,
  LayoutDashboard,
  Store,
  LineChart,
  Wallet,
} from "lucide-react";
import { isMobile, LOGO_URL } from "../lib/config";
import { useAuth } from "../context/AuthContext";
import { InstallBanner } from "./InstallBanner";
import { MobileBottomNav } from "./MobileBottomNav";
import { NotificationBell } from "./NotificationBell";

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/vault', icon: Database, label: 'Data Vault' },
    { path: '/valuation', icon: Sparkles, label: 'AI Valuation' },
    { path: '/market', icon: Store, label: 'Marketplace' },
    { path: '/earnings', icon: Wallet, label: 'Earnings' },
    { path: '/analytics', icon: LineChart, label: 'Analytics' },
    { path: '/privacy', icon: Shield, label: 'Privacy' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Mobile header */}
      <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-slate-900/80 backdrop-blur-lg border-b border-purple-500/20 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-white/10 rounded-lg">
            <Menu className="w-5 h-5 text-gray-300" />
          </button>
          <img src={LOGO_URL} alt="DigitalMe" className="h-8 w-8 rounded-lg object-cover" />
          <span className="font-semibold text-white">DigitalMe</span>
        </div>
        <NotificationBell />
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-slate-900/95 backdrop-blur-lg border-r border-purple-500/20 z-50 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
          <div className="p-6 border-b border-purple-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="DigitalMe" className="h-10 w-10 rounded-xl object-cover" />
                <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  DigitalMe
                </span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="p-4 flex-1 overflow-y-auto h-[calc(100vh-200px)]">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition ${
                    location.pathname === item.path
                      ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 text-cyan-400 border border-purple-500/30'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-purple-500/20 bg-slate-900/95">
            <div className="hidden lg:block mb-4">
              <NotificationBell />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-sm text-gray-400 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 lg:p-8 min-h-screen lg:ml-0 pb-24 lg:pb-8">{children}</main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Mobile Bottom Navigation */}
      {isMobileView && <MobileBottomNav />}

      {/* Install Banner */}
      <InstallBanner />
    </div>
  );
};

// Dashboard Page
